import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Check, Download, RotateCcw, Search, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { AdminTextarea, adminInputClass } from '@/components/admin/AdminFormField';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAdminFetch } from '@/hooks/useAdminFetch';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/admin/contacts')({
  component: AdminContactsPage,
});

type Contact = {
  id: number;
  companyName: string;
  email: string;
  name: string | null;
  intent: 'audit' | 'operations' | 'investor' | 'partnership' | 'press' | 'other' | null;
  brandUrl: string | null;
  marketingConsent: boolean | null;
  businessScale: string | null;
  industry: string | null;
  solutionsInterest: string | null;
  preferredConsultTime: string | null;
  description: string | null;
  status: 'pending' | 'processed';
  internalNotes: string | null;
  createdAt: string;
};

const INTENT_LABELS: Record<NonNullable<Contact['intent']>, string> = {
  audit: 'Audit',
  operations: 'Operations',
  investor: 'Investor',
  partnership: 'Partnership',
  press: 'Press',
  other: 'Other',
};

const INTENT_COLORS: Record<NonNullable<Contact['intent']>, string> = {
  audit: 'border-purple-500/30 bg-purple-500/20 text-purple-300',
  operations: 'border-blue-500/30 bg-blue-500/20 text-blue-300',
  investor: 'border-pink-500/30 bg-pink-500/20 text-pink-300',
  partnership: 'border-indigo-500/30 bg-indigo-500/20 text-indigo-300',
  press: 'border-amber-500/30 bg-amber-500/20 text-amber-300',
  other: 'border-gray-500/30 bg-gray-500/20 text-gray-300',
};

function AdminContactsPage() {
  const { fetchAdmin } = useAdminFetch();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 400);
  const [status, setStatus] = useState<string>('');
  const [intentFilter, setIntentFilter] = useState<string>('');
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [statusDraft, setStatusDraft] = useState<'pending' | 'processed'>('pending');
  const [notesDraft, setNotesDraft] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);

  useEffect(() => {
    void debouncedSearch;
    setPage(1);
  }, [debouncedSearch]);

  const { data, isLoading } = useQuery({
    queryKey: ['admin-contacts', page, debouncedSearch, status, intentFilter],
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(page), limit: '15' });
      if (debouncedSearch) params.set('search', debouncedSearch);
      if (status) params.set('status', status);
      if (intentFilter) params.set('intent', intentFilter);
      const res = await fetchAdmin(`/api/admin/contacts?${params}`);
      if (!res.ok) throw new Error('Failed to fetch');
      const json = await res.json();
      return json.data;
    },
  });

  const contacts = data?.items ?? [];
  const totalPages = data?.totalPages ?? 1;

  const updateMutation = useMutation({
    mutationFn: async ({
      id,
      status: s,
      internalNotes,
    }: {
      id: number;
      status?: 'pending' | 'processed';
      internalNotes?: string;
    }) => {
      const res = await fetchAdmin(`/api/admin/contacts/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: s, internalNotes }),
      });
      if (!res.ok) throw new Error('Update failed');
      return res.json();
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ['admin-contacts'] });
      if (selectedContact?.id === vars.id) {
        setSelectedContact((prev) =>
          prev
            ? {
                ...prev,
                status: vars.status ?? prev.status,
                internalNotes: vars.internalNotes ?? prev.internalNotes,
              }
            : null
        );
        if (vars.status !== undefined) setStatusDraft(vars.status);
        if (vars.internalNotes !== undefined) setNotesDraft(vars.internalNotes);
      }
    },
  });

  const handleSaveChanges = async () => {
    if (!selectedContact) return;
    setSavingNotes(true);
    try {
      await updateMutation.mutateAsync({
        id: selectedContact.id,
        status: statusDraft,
        internalNotes: notesDraft,
      });
    } finally {
      setSavingNotes(false);
    }
  };

  const normalizedSelectedStatus = selectedContact && String(selectedContact.status).toLowerCase();
  const hasPanelChanges =
    selectedContact &&
    (statusDraft !== normalizedSelectedStatus ||
      notesDraft !== (selectedContact.internalNotes ?? ''));

  const handleChangeStatus = (contact: Contact, newStatus: 'pending' | 'processed') => {
    updateMutation.mutate({ id: contact.id, status: newStatus });
    if (selectedContact?.id === contact.id) {
      setSelectedContact((prev) => (prev ? { ...prev, status: newStatus } : null));
    }
  };

  const handleExport = async () => {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    const res = await fetchAdmin(`/api/admin/contacts/export?${params}`);
    if (!res.ok) throw new Error('Export failed');
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'contacts.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const openDetail = (contact: Contact) => {
    setSelectedContact(contact);
    setStatusDraft(String(contact.status).toLowerCase() === 'processed' ? 'processed' : 'pending');
    setNotesDraft(contact.internalNotes ?? '');
  };

  const isInitialLoading = isLoading && !data;

  return (
    <div>
      {isInitialLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500" />
        </div>
      ) : (
        <>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <h1 className="text-2xl font-bold text-white">Contact management</h1>
            <Button variant="outline" onClick={handleExport} className="flex items-center gap-2">
              <Download className="size-4" />
              Export CSV
            </Button>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-gray-500" />
              <Input
                type="text"
                placeholder="Search by email or company..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className={cn('w-full pl-10', adminInputClass)}
              />
            </div>
            <Select value={status || 'all'} onValueChange={(v) => setStatus(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[180px] border-gray-600 bg-gray-800 text-white hover:bg-gray-700/50 focus:ring-2 focus:ring-purple-500">
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent className="border-gray-700 bg-gray-800 text-white [&_[data-slot=select-item]]:focus:bg-gray-700">
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="processed">Processed</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={intentFilter || 'all'}
              onValueChange={(v) => setIntentFilter(v === 'all' ? '' : v)}
            >
              <SelectTrigger className="w-[180px] border-gray-600 bg-gray-800 text-white hover:bg-gray-700/50 focus:ring-2 focus:ring-purple-500">
                <SelectValue placeholder="All intents" />
              </SelectTrigger>
              <SelectContent className="border-gray-700 bg-gray-800 text-white [&_[data-slot=select-item]]:focus:bg-gray-700">
                <SelectItem value="all">All intents</SelectItem>
                {Object.entries(INTENT_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-800">
                    <th className="text-left px-6 py-4 text-sm font-medium text-gray-400">
                      Company
                    </th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-gray-400">Email</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-gray-400">
                      Intent
                    </th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-gray-400">
                      Brand URL
                    </th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-gray-400">Date</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-gray-400">
                      Status
                    </th>
                    <th className="text-right px-6 py-4 text-sm font-medium text-gray-400">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {contacts.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                        No contacts yet
                      </td>
                    </tr>
                  ) : (
                    contacts.map((contact: Contact) => (
                      <tr
                        key={contact.id}
                        className="border-b border-gray-800 hover:bg-gray-800/50 cursor-pointer"
                        onClick={() => openDetail(contact)}
                      >
                        <td className="px-6 py-4 text-white font-medium">{contact.companyName}</td>
                        <td className="px-6 py-4 text-gray-400">{contact.email}</td>
                        <td className="px-6 py-4">
                          {contact.intent ? (
                            <Badge variant="outline" className={INTENT_COLORS[contact.intent]}>
                              {INTENT_LABELS[contact.intent]}
                            </Badge>
                          ) : (
                            <span className="text-gray-500">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-gray-400 max-w-[180px] truncate">
                          {contact.brandUrl ?? '-'}
                        </td>
                        <td className="px-6 py-4 text-gray-400">
                          {new Date(contact.createdAt).toLocaleDateString('en-US')}
                        </td>
                        <td className="px-6 py-4">
                          <Badge
                            variant="outline"
                            className={
                              contact.status === 'processed'
                                ? 'border-green-500/30 bg-green-500/20 text-green-400'
                                : 'border-yellow-500/30 bg-yellow-500/20 text-yellow-400'
                            }
                          >
                            {contact.status}
                          </Badge>
                        </td>
                        <td className="px-6 py-4 text-right" onKeyDown={(e) => e.stopPropagation()}>
                          <div className="flex justify-end gap-1">
                            {contact.status === 'pending' ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleChangeStatus(contact, 'processed');
                                }}
                                disabled={updateMutation.isPending}
                                className="text-green-400 hover:text-green-300"
                                title="Mark as processed"
                              >
                                <Check className="size-4" />
                              </Button>
                            ) : (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleChangeStatus(contact, 'pending');
                                }}
                                disabled={updateMutation.isPending}
                                className="text-yellow-400 hover:text-yellow-300"
                                title="Mark as pending"
                              >
                                <RotateCcw className="size-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="flex justify-center gap-2 px-6 py-4 border-t border-gray-800">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                >
                  Prev
                </Button>
                <span className="flex items-center px-4 text-gray-400">
                  {page} / {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                >
                  Next
                </Button>
              </div>
            )}
          </div>

          {/* Detail side panel */}
          {selectedContact && (
            <div
              className="fixed inset-0 z-50 flex justify-end bg-black/50"
              role="dialog"
              aria-modal="true"
              aria-label="Contact detail"
            >
              <button
                type="button"
                className="absolute inset-0 cursor-default"
                onClick={() => setSelectedContact(null)}
                aria-label="Close"
              />
              <div className="relative z-10 w-full max-w-lg bg-gray-900 border-l border-gray-800 shadow-xl overflow-y-auto">
                <div className="p-6 border-b border-gray-800 flex items-center justify-between">
                  <h2 className="text-lg font-bold text-white">Contact detail</h2>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setSelectedContact(null)}
                    className="text-gray-400"
                  >
                    <X className="size-5" />
                  </Button>
                </div>
                <div className="p-6 space-y-4">
                  {/* Trạng thái & Lưu - đặt đầu để dễ thấy */}
                  <div className="pb-4 border-b border-gray-800 space-y-3">
                    <div>
                      <span className="text-sm text-gray-500 block mb-2">Status</span>
                      <div className="flex flex-wrap gap-2">
                        {statusDraft === 'pending' ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setStatusDraft('processed')}
                            disabled={updateMutation.isPending}
                            className="border-yellow-500/30 bg-yellow-500/10 text-yellow-400 hover:bg-yellow-500/20"
                          >
                            <Check className="size-4 mr-1" />
                            Mark as processed
                          </Button>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setStatusDraft('pending')}
                            disabled={updateMutation.isPending}
                            className="border-green-500/30 bg-green-500/10 text-green-400 hover:bg-green-500/20"
                          >
                            <RotateCcw className="size-4 mr-1" />
                            Mark as pending
                          </Button>
                        )}
                        <Badge
                          variant="outline"
                          className={
                            statusDraft === 'processed'
                              ? 'border-green-500/30 bg-green-500/20 text-green-400'
                              : 'border-yellow-500/30 bg-yellow-500/20 text-yellow-400'
                          }
                        >
                          {statusDraft === 'processed' ? 'Processed' : 'Pending'}
                        </Badge>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={handleSaveChanges}
                      disabled={!hasPanelChanges || savingNotes || updateMutation.isPending}
                    >
                      {savingNotes || updateMutation.isPending ? 'Saving…' : 'Save changes'}
                    </Button>
                  </div>

                  <div>
                    <span className="text-sm text-gray-500">Company</span>
                    <p className="text-white font-medium">{selectedContact.companyName}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Name</span>
                    <p className="text-gray-300">{selectedContact.name ?? '-'}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Email</span>
                    <p className="text-white">{selectedContact.email}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Intent</span>
                    <p>
                      {selectedContact.intent ? (
                        <Badge variant="outline" className={INTENT_COLORS[selectedContact.intent]}>
                          {INTENT_LABELS[selectedContact.intent]}
                        </Badge>
                      ) : (
                        <span className="text-gray-300">-</span>
                      )}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Brand URL</span>
                    <p className="text-gray-300 break-all">{selectedContact.brandUrl ?? '-'}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Marketing consent</span>
                    <p className="text-gray-300">
                      {selectedContact.marketingConsent ? 'Yes' : 'No'}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Message</span>
                    <p className="text-gray-300 whitespace-pre-wrap">
                      {selectedContact.description ?? '-'}
                    </p>
                  </div>
                  {(selectedContact.businessScale ||
                    selectedContact.industry ||
                    selectedContact.solutionsInterest ||
                    selectedContact.preferredConsultTime) && (
                    <details className="text-sm">
                      <summary className="cursor-pointer text-gray-500 mb-2">
                        Legacy fields (pre-v2.1)
                      </summary>
                      <div className="space-y-2 pl-2 border-l border-gray-800">
                        <p className="text-gray-400">
                          Business scale: {selectedContact.businessScale ?? '-'}
                        </p>
                        <p className="text-gray-400">Industry: {selectedContact.industry ?? '-'}</p>
                        <p className="text-gray-400">
                          Solutions interest: {selectedContact.solutionsInterest ?? '-'}
                        </p>
                        <p className="text-gray-400">
                          Preferred consult time: {selectedContact.preferredConsultTime ?? '-'}
                        </p>
                      </div>
                    </details>
                  )}
                  <div>
                    <span className="text-sm text-gray-500">Created at</span>
                    <p className="text-gray-400">
                      {new Date(selectedContact.createdAt).toLocaleString('en-US')}
                    </p>
                  </div>

                  <div className="pt-4 border-t border-gray-800">
                    <AdminTextarea
                      id="internalNotes"
                      label="Internal notes"
                      value={notesDraft}
                      onChange={(e) => setNotesDraft(e.target.value)}
                      rows={3}
                      className="resize-none"
                      placeholder="Notes for the team..."
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
